import type { ComponentChildren } from 'preact';
import { useEffect } from 'preact/hooks';
import { bootstrapSession, sessionSignal, webHmiExpiredSignal } from '../auth/session';
import './AuthGuard.css';

interface Props {
  children: ComponentChildren;
}

/** Ensures we have a session (or that the login redirect has fired) before
 *  rendering the app. On loading we show a small centred spinner. On 401 the
 *  bootstrap has already navigated away, so we render nothing to avoid a
 *  flash before the browser leaves the page. */
export function AuthGuard({ children }: Props) {
  useEffect(() => {
    void bootstrapSession();
  }, []);

  const state = sessionSignal.value;

  if (state.loading) return <FullScreenLoader />;
  if (!state.user && webHmiExpiredSignal.value) {
    return (
      <div class="hv-auth-error">
        <h1>Fernbedienung beendet</h1>
        <p>Die Fernbedienung endet aus Sicherheitsgründen nach wenigen Minuten. Für eine dauerhafte Anzeige ist das Bedienfeld an der Anlage da.</p>
        <p><a href="/api/auth/login?returnUrl=%2Fm">Neu anmelden</a></p>
      </div>
    );
  }
  if (!state.user) {
    // bootstrap either redirected already (loading was true and we transitioned
    // to !loading via error) or hit a non-401 error. Show a minimal message
    // instead of a blank page — helps operators notice the API is unreachable.
    return (
      <div class="hv-auth-error">
        <h1>Cannot reach the API</h1>
        <p>Try reloading. If the problem persists, contact your operator.</p>
      </div>
    );
  }
  // Erste Anmeldung ohne vorbereiteten Eintrag landet in der Gruppe "Ohne
  // Freigabe" (keine Rechte, kein Datenbereich; seit 18.10.2026 statt Admins).
  // Statt einer leeren HVACloud ein klarer Hinweis.
  if (!state.user.webHmiOnly && state.user.dataScopes.length === 0 && state.user.permissions.length === 0) {
    return (
      <div class="hv-auth-error">
        <h1>Konto noch nicht freigeschaltet</h1>
        <p>Du bist angemeldet als {state.user.email ?? state.user.displayName}, hast aber noch keine Rechte.
          Bitte wende dich an deinen Ansprechpartner, er schaltet dein Konto frei.</p>
        <p><a href="/api/auth/logout">Abmelden</a></p>
      </div>
    );
  }
  // Endkunden (nur Web-HMI) sehen das Dashboard nie: jede andere Seite fuehrt
  // auf die Web-HMI. Der Server liefert ihnen dort ohnehin nichts.
  if (state.user.webHmiOnly && !/^\/m(\/|$)/.test(window.location.pathname)) {
    window.location.replace('/m');
    return <FullScreenLoader />;
  }
  return <>{children}</>;
}

function FullScreenLoader() {
  return (
    <div class="hv-loader">
      <div class="hv-loader__spinner" />
    </div>
  );
}
